import { ArrowRight, Check, ChevronDown, ChevronLeft, ChevronRight, Filter, X } from 'lucide-react';
import { DialogClose, DialogTitle } from '@/components/ui/Dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/Dropdown';
import { cn } from '@/utils/cn';
import { formatDateTime } from '../../helpers';
import { DiffStat } from './DiffStat';
import type { ApprovalDiff, DiffStats } from './types';

interface CompareHeaderProps {
  diff: ApprovalDiff;
  typeLabel: string;
  targetWho?: string;
  totals: DiffStats;
  changedSections: number;
  canJump: boolean;
  onBaseChange: (versionId: string) => void;
  onlyChanges: boolean;
  onOnlyChangesToggle: () => void;
  split: boolean;
  onSplitChange: (split: boolean) => void;
  onJump: (dir: 1 | -1) => void;
}

export const CompareHeader = ({
  diff,
  typeLabel,
  targetWho,
  totals,
  changedSections,
  canJump,
  onBaseChange,
  onlyChanges,
  onOnlyChangesToggle,
  split,
  onSplitChange,
  onJump,
}: CompareHeaderProps) => {
  const summary = `${changedSections} ${
    changedSections === 1
      ? 'změněná část'
      : changedSections >= 2 && changedSections <= 4
      ? 'změněné části'
      : 'změněných částí'
  }`;
  return (
    <div className="px-5 pt-3.5 pb-3 border-b border-neutral-150 bg-neutral-25 shrink-0">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[11px] text-neutral-500">
            <span>{typeLabel}</span>
            <ChevronRight size={10} className="text-neutral-300" />
            <span>{diff.object_title}</span>
          </div>
          <DialogTitle className="mt-0.5 text-[15px] font-semibold tracking-tight text-neutral-900 truncate">
            Porovnání verzí — {diff.object_title}
          </DialogTitle>
        </div>
        <DialogClose asChild>
          <button
            type="button"
            data-testid="compare-close-x-btn"
            className="h-7 w-7 inline-flex items-center justify-center rounded text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900"
          >
            <X size={14} />
          </button>
        </DialogClose>
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="compare-base-picker"
              className="h-8 pl-2.5 pr-2 inline-flex items-center gap-2 rounded border border-neutral-200 bg-white hover:bg-neutral-50"
            >
              <span className="text-[12.5px] font-medium text-neutral-900 font-mono">
                {diff.base?.label ?? '—'}
              </span>
              <span className="text-[11px] text-neutral-500">
                {diff.base
                  ? `${formatDateTime(diff.base.created_on)}${
                      diff.base.note ? ` · ${diff.base.note}` : ''
                    }`
                  : 'první verze'}
              </span>
              <ChevronDown size={12} className="text-neutral-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuLabel>Porovnat proti</DropdownMenuLabel>
            {diff.revisions
              .filter((rev) => rev.version_id !== diff.target.version_id)
              .map((rev) => (
                <DropdownMenuItem key={rev.version_id} onClick={() => onBaseChange(rev.version_id)}>
                  {diff.base?.version_id === rev.version_id ? (
                    <Check size={12} />
                  ) : (
                    <span className="w-3 inline-block" />
                  )}
                  <span className="font-mono">{rev.label}</span>
                  <span className="text-neutral-500">· {formatDateTime(rev.created_on)}</span>
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <ArrowRight size={13} className="text-neutral-400" />
        <span className="h-8 pl-2.5 pr-3 inline-flex items-center gap-2 rounded border border-primary-600 bg-primary-50">
          <span className="text-[12.5px] font-medium text-primary-700 font-mono">
            {diff.target.label}
          </span>
          <span className="text-[11px] text-primary-700/80">
            ke schválení{targetWho ? ` · ${targetWho}` : ''}
          </span>
        </span>

        <span className="ml-1 inline-flex items-center gap-2 pl-2.5 pr-3 h-8 rounded bg-white border border-neutral-200">
          <span className="text-[11px] text-neutral-500">{summary}</span>
          <DiffStat {...totals} />
        </span>

        <div className="ml-auto flex items-center gap-1.5">
          <div className="inline-flex items-center rounded border border-neutral-200 bg-white overflow-hidden">
            <button
              type="button"
              onClick={() => onJump(-1)}
              disabled={!canJump}
              title="Předchozí změna"
              data-testid="compare-prev-change-btn"
              className="h-8 w-7 inline-flex items-center justify-center text-neutral-500 hover:bg-neutral-50"
            >
              <ChevronLeft size={13} />
            </button>
            <span className="px-2 text-[11px] font-mono text-neutral-600 tabular-nums border-x border-neutral-150 h-8 inline-flex items-center">
              Změny
            </span>
            <button
              type="button"
              onClick={() => onJump(1)}
              disabled={!canJump}
              title="Další změna"
              data-testid="compare-next-change-btn"
              className="h-8 w-7 inline-flex items-center justify-center text-neutral-500 hover:bg-neutral-50"
            >
              <ChevronRight size={13} />
            </button>
          </div>
          <button
            type="button"
            onClick={onOnlyChangesToggle}
            aria-pressed={onlyChanges}
            data-testid="compare-only-changes-btn"
            className={cn(
              'h-8 px-2.5 inline-flex items-center gap-1.5 rounded border text-[12px] font-medium transition-colors',
              onlyChanges
                ? 'bg-primary-50 border-primary-600 text-primary-700'
                : 'bg-white border-neutral-200 text-neutral-600 hover:bg-neutral-50'
            )}
          >
            <Filter size={12} />
            Jen změny
          </button>
          <div className="inline-flex p-0.5 rounded bg-neutral-100">
            {(
              [
                [true, 'Vedle sebe', 'compare-view-split-btn'],
                [false, 'Pod sebou', 'compare-view-unified-btn'],
              ] as const
            ).map(([value, label, testId]) => (
              <button
                key={testId}
                type="button"
                onClick={() => onSplitChange(value)}
                data-testid={testId}
                className={cn(
                  'h-7 px-2.5 rounded text-[11.5px] font-medium transition-colors',
                  split === value
                    ? 'bg-white text-neutral-900 shadow-sm-soft'
                    : 'text-neutral-600 hover:text-neutral-900'
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
